import { useSuspenseQuery } from '@apollo/client';
import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { eluveAdminHasuraContext } from '@eluve/apollo-client';
import {
  Badge,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
  Text,
} from '@eluve/components';
import { getAllSummariesForEluveAdmin } from '@eluve/frontend-appointment-hooks';
import { DbEnums } from '@eluve/graphql-types';
import {
  DynamicArtifactTemplate,
  LegacySOAPOutputTemplate,
  dynamicArtifactTemplateSchema,
  hydrateDynamicArtifactTemplate,
} from '@eluve/llm-outputs';
import { DynamicArtifactRenderer } from '@eluve/smart-blocks';

import { SummaryGenerationInfoCard } from './SummaryGenerationInfoCard';
import { resolveActiveSummaryVersion } from './activeSummaryVersion';

type StatusVariant = 'success' | 'destructive' | 'secondary' | 'info';

const statusBadgeVariant = (status: string): StatusVariant => {
  switch (status) {
    case DbEnums.WorkerStatus.COMPLETED:
      return 'success';
    case DbEnums.WorkerStatus.FAILED:
    case DbEnums.WorkerStatus.FAILED_WORKER_TERMINATED:
      return 'destructive';
    case DbEnums.WorkerStatus.CANCELLED:
      return 'secondary';
    case DbEnums.WorkerStatus.IN_PROGRESS:
    default:
      return 'info';
  }
};

/**
 * If the llm_outputs row is stuck at IN_PROGRESS but the parent
 * appointment_llm_jobs row has already terminated, trust the job's status.
 * Backend bug tracked in ENG-3837.
 */
const effectiveStatus = (
  outputStatus: string,
  jobStatus: string | null | undefined,
): string => {
  if (outputStatus !== DbEnums.WorkerStatus.IN_PROGRESS) return outputStatus;
  if (jobStatus === DbEnums.WorkerStatus.FAILED_WORKER_TERMINATED)
    return DbEnums.WorkerStatus.FAILED;
  if (
    jobStatus === DbEnums.WorkerStatus.FAILED ||
    jobStatus === DbEnums.WorkerStatus.CANCELLED ||
    jobStatus === DbEnums.WorkerStatus.COMPLETED
  ) {
    return jobStatus;
  }
  return outputStatus;
};

/**
 * Failed llm_outputs rows store a human-readable message in
 * `content.failureReason` alongside the categorical `failureType`.
 */
const readFailureReason = (
  content: Record<string, unknown> | null | undefined,
): string | null => {
  const reason = content?.['failureReason'];
  return typeof reason === 'string' ? reason : null;
};

const timestampFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
  timeZoneName: 'short',
});

const versionLabel = ({
  completedAt,
  startedAt,
  createdAt,
  modelVersion,
}: {
  completedAt: string | null;
  startedAt: string | null;
  createdAt: string;
  modelVersion: string | null;
}): string => {
  const ts = completedAt ?? startedAt ?? createdAt;
  const formatted = timestampFormatter.format(new Date(ts));
  return modelVersion ? `${formatted} · ${modelVersion}` : formatted;
};

export const AdminSummaryContent: React.FC = () => {
  const { tenantId, appointmentId } = useParams() as {
    tenantId: string;
    appointmentId: string;
  };

  const {
    data: { appointmentsByPk: appointment },
  } = useSuspenseQuery(getAllSummariesForEluveAdmin, {
    variables: { tenantId, appointmentId },
    context: eluveAdminHasuraContext,
  });

  const summaries = appointment?.appointment_llm_outputs ?? [];

  const {
    defaultSelectedId,
    activeLlmOutputId,
    latestLlmOutputId,
    showLatestTag,
  } = resolveActiveSummaryVersion(
    summaries.map((s) => s.llmOutputId),
    appointment?.humanOutputs[0]?.output?.editedFromLlmOutputId,
  );

  const [selectedId, setSelectedId] = useState<string | undefined>(
    defaultSelectedId,
  );

  const selected =
    summaries.find((s) => s.llmOutputId === selectedId) ??
    summaries.find((s) => s.llmOutputId === defaultSelectedId) ??
    summaries[0];

  const output = selected?.output;
  const metadata = output?.metadata;
  const outputType = output?.modelPromptTemplate?.template?.outputType;

  const outputTemplate = useMemo<DynamicArtifactTemplate | undefined>(() => {
    if (outputType === DbEnums.LlmOutputType.SOAP_NOTE) {
      return LegacySOAPOutputTemplate;
    }
    const raw = metadata?.output_variant?.template;
    if (!raw) return undefined;
    const parsed = dynamicArtifactTemplateSchema.safeParse(raw);
    return parsed.success ? parsed.data : undefined;
  }, [outputType, metadata?.output_variant?.template]);

  // Only render content for COMPLETED runs. Failed/cancelled/in-progress runs
  // sometimes have a non-null content object (e.g. a `{ failureReason }` stub)
  // that would hydrate into empty template sections — which looks like a
  // successful but blank summary. Show the placeholder + failure reason instead.
  const selectedStatus = output
    ? effectiveStatus(output.status, selected?.job?.jobStatus)
    : null;
  const hydrated = useMemo(() => {
    if (
      selectedStatus !== DbEnums.WorkerStatus.COMPLETED ||
      !outputTemplate ||
      !output?.content
    ) {
      return null;
    }
    return hydrateDynamicArtifactTemplate(outputTemplate, output.content);
  }, [selectedStatus, outputTemplate, output?.content]);

  if (!selected || !output || !selectedStatus) {
    return (
      <Stack gap={2} className="p-4">
        <Text size="md">
          No summaries have been generated for this appointment.
        </Text>
      </Stack>
    );
  }

  return (
    <Stack gap={4} className="p-4" align="stretch">
      <Stack direction="horizontal" gap={3} align="center" fullWidth={false}>
        <Label>Summary version</Label>
        <Select value={selected.llmOutputId} onValueChange={setSelectedId}>
          <SelectTrigger className="w-auto min-w-[360px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {summaries.map((s) => {
              const o = s.output;
              const status = effectiveStatus(o.status, s.job?.jobStatus);
              const label = versionLabel({
                completedAt: o.completedAt,
                startedAt: o.startedAt,
                createdAt: o.createdAt,
                modelVersion:
                  (o.modelPromptTemplate?.model_args?.args?.['model'] as
                    | string
                    | undefined) ?? null,
              });
              const isActive = s.llmOutputId === activeLlmOutputId;
              const isLatest =
                showLatestTag && s.llmOutputId === latestLlmOutputId;
              return (
                <SelectItem key={s.llmOutputId} value={s.llmOutputId}>
                  <div className="flex items-center gap-2">
                    <div className="w-[130px] shrink-0">
                      <Badge variant={statusBadgeVariant(status)}>
                        {status}
                      </Badge>
                    </div>
                    <span>{label}</span>
                    {isActive && <Badge variant="default">Active</Badge>}
                    {isLatest && <Badge variant="outline">Latest</Badge>}
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </Stack>

      <Stack direction="horizontal" gap={4} align="start">
        <Stack gap={4} className="flex-1" align="stretch">
          {hydrated ? (
            <DynamicArtifactRenderer isReadonly blocks={hydrated.blocks} />
          ) : (
            <Stack
              gap={2}
              className="rounded-lg border border-border-warning-subtle bg-bg-warning-subtle p-3 text-sm text-text-warning-subtle"
              align="stretch"
            >
              <Text size="md">
                No content available — status: {selectedStatus}
              </Text>
              <FailureDetails
                failureType={output.failureType}
                failureReason={readFailureReason(output.content)}
                status={selectedStatus}
              />
            </Stack>
          )}
        </Stack>
        <div className="w-[380px] shrink-0">
          <SummaryGenerationInfoCard
            promptTemplate={metadata?.prompt_template}
            promptVariantNumber={metadata?.prompt_variant?.variantNumber}
            outputTemplate={metadata?.output_template}
            outputVariantNumber={metadata?.output_variant?.variantNumber}
            modelArgs={output.modelPromptTemplate?.model_args}
            startedAt={output.startedAt}
            completedAt={output.completedAt}
          />
        </div>
      </Stack>
    </Stack>
  );
};

interface FailureDetailsProps {
  failureType: string | null | undefined;
  failureReason: string | null;
  status: string;
}

const FailureDetails: React.FC<FailureDetailsProps> = ({
  failureType,
  failureReason,
  status,
}) => {
  if (failureType || failureReason) {
    return (
      <Text size="sm">
        {failureType && <strong>{failureType}</strong>}
        {failureType && failureReason ? ' — ' : ''}
        {failureReason}
      </Text>
    );
  }
  if (status === DbEnums.WorkerStatus.FAILED) {
    return <Text size="sm">Failure reason not recorded.</Text>;
  }
  return null;
};
